import React, { useCallback, useState } from 'react';

import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import { Button } from '@picsio/ui';

import localization from '../../../../shared/strings';
import Checkbox from '../../../../UIComponents/checkbox';
import Icon from '../../../Icon';
import s from './styles.module.scss';

function UpdateSharedAltTextDialog({
  productTitle,
  affectedUsages,
  onConfirm,
  onCancel,
  root,
}) {
  const strings = localization.ALT_TEXT.UPDATE_SHARED;
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const destroy = useCallback(() => {
    try {
      root.unmount();
    } catch {
      // ignore
    }
  }, [root]);

  const handleCancel = useCallback(() => {
    destroy();
    onCancel?.();
  }, [destroy, onCancel]);

  const handleConfirm = useCallback(() => {
    destroy();
    onConfirm?.(dontShowAgain);
  }, [destroy, dontShowAgain, onConfirm]);

  return (
    <div className={`simpleDialog ${s.dialog}`}>
      <div className="simpleDialogUnderlayer" />
      <div className={`simpleDialogBox ${s.dialogBox}`}>
        <div className="simpleDialogHeader">
          <span className="simpleDialogTitle">{strings.TITLE}</span>
          <span
            role="button"
            tabIndex={0}
            className="simpleDialogBtnCross"
            onClick={handleCancel}
            onKeyDown={handleCancel}
          >
            <Icon name="close" />
          </span>
        </div>
        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <p className={s.description}>
              {strings.DESCRIPTION(productTitle)}
            </p>
            <If condition={affectedUsages.length > 0}>
              <p className={s.affectedUsagesTitle}>{strings.AFFECTED_USAGES}</p>
              <ul className={s.affectedUsagesList}>
                {affectedUsages.map((usage) => (
                  <li key={usage} className={s.affectedUsageItem}>
                    {usage}
                  </li>
                ))}
              </ul>
            </If>
            <div className={s.checkboxRow}>
              <Checkbox
                label={strings.DONT_SHOW_AGAIN}
                value={dontShowAgain}
                onChange={setDontShowAgain}
              />
            </div>
          </div>
        </div>
        <div className={`simpleDialogFooter ${s.footer}`}>
          <Button color="secondary" size="md" variant="contained" onClick={handleCancel}>
            {strings.BTN_CANCEL}
          </Button>
          <Button color="primary" size="md" variant="contained" onClick={handleConfirm}>
            {strings.BTN_OK}
          </Button>
        </div>
      </div>
    </div>
  );
}

UpdateSharedAltTextDialog.propTypes = {
  productTitle: PropTypes.string.isRequired,
  affectedUsages: PropTypes.arrayOf(PropTypes.string).isRequired,
  root: PropTypes.shape({
    unmount: PropTypes.func.isRequired,
  }).isRequired,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
};

UpdateSharedAltTextDialog.defaultProps = {
  onConfirm: undefined,
  onCancel: undefined,
};

export function showUpdateSharedAltTextDialog({
  productTitle,
  affectedUsages,
}) {
  const $wrapper = document.querySelector('.wrapperDialog');
  const root = createRoot($wrapper);

  return new Promise((resolve, reject) => {
    root.render(
      <UpdateSharedAltTextDialog
        productTitle={productTitle}
        affectedUsages={affectedUsages}
        root={root}
        onConfirm={(dontShowAgain) => resolve(dontShowAgain)}
        onCancel={() => reject(new Error('cancel'))}
      />,
    );
  });
}

export default UpdateSharedAltTextDialog;
